import { useVerifyLayout } from '../../../../hooks/useVerifyLayout/useVerifyLayout';
import { useMobileVerifyContext } from '../../../../context/MobileVerifyContext';
import { SubscriptionApiResponse, SubscriptionTypes } from '../../../../api/subscriptions/types';
import { useSubscriptionApi } from '../../../../hooks/subscriptionApi';
import { perksAlertsEnum } from '../../../../../perks/constants';
import { SubscriptionsClient } from '../../../../api/subscriptions/SubscriptionsClient';
import React, { useEffect, ReactNode } from 'react';
// @ts-ignore
import { useHistory } from 'react-router-dom';
import ReceiptHelper from '../../../../../receipts/helpers/receipt.helper';
import {
  checkIsPerksRedirectSetAndNavigate,
  getPerksRedirectDestination,
  setPerksSignupError,
  setPerksSubscribeSuccessful,
} from '../../../../../perksInfo/utils';

const receiptHelper = new ReceiptHelper();
export const PERKS_FINISHING_UP_MESSAGE = 'Joining JB Perks...';

const PerksSignup = ({ children }: { children: ReactNode }) => {
  const { setThemeContext, themeContext, loadingMessage, setLoadingMessage } = useVerifyLayout();
  const {
    data: { isFinished: isMobileVerifyFinished, isJustVerified, currentMobileNumber },
  } = useMobileVerifyContext();

  const {
    subscriptions: { [SubscriptionTypes.perks]: currentPerksSubscription },
    customer,
    customerLoading,
    customerError,
    linkedId,
  } = useSubscriptionApi();
  const { id: customerId, birthMonth } = customer;
  const history = useHistory();

  const getWelcomeAlert = (): perksAlertsEnum => {
    if (currentPerksSubscription?.isActive) {
      if (themeContext?.birthMonthSelected) {
        return perksAlertsEnum.birthdayAdded;
      } else if (themeContext?.birthMonthExists) {
        return perksAlertsEnum.birthdayAlreadySaved;
      }
      return perksAlertsEnum.isAlreadyMember;
    }
    if (currentPerksSubscription?.id) {
      if (themeContext?.birthMonthSelected) {
        return perksAlertsEnum.isOldMemberWithBirthday;
      } else if (themeContext?.birthMonthExists) {
        return perksAlertsEnum.isOldMemberWithBirthdayAlreadySaved;
      }
      return perksAlertsEnum.isOldMember;
    }
    if (themeContext?.birthMonthSelected) {
      return perksAlertsEnum.isNewMemberWithBirthday;
    }
    return perksAlertsEnum.isNewMember;
  };

  const getErrorAlert = (errorCode?: number): string => {
    switch (errorCode) {
      case SubscriptionsClient.errorCodes.tooManyRequests:
        return 'phoneVerifiedTooManyTimes';
      case SubscriptionsClient.errorCodes.unauthorised:
      default:
        return 'perksAuthError';
    }
  };

  const createSubscription = async (): Promise<SubscriptionApiResponse | undefined> => {
    if (currentPerksSubscription?.isActive) return;

    return await SubscriptionsClient.createSubscription({
      subscriptionType: SubscriptionTypes.perks,
      customerId,
      linkedId,
      parentId: currentPerksSubscription?.id,
      sourceLocation: themeContext?.source,
      storeId: themeContext?.storeId,
    });
  };

  const handleFinish = async () => {
    let alert: string;
    if (!customerError && !currentPerksSubscription?.error) {
      const createSubResponse = await createSubscription();
      alert = createSubResponse?.error ? getErrorAlert(createSubResponse?.statusCode) : getWelcomeAlert();
      if (getPerksRedirectDestination()) {
        if (!createSubResponse?.error || (createSubResponse?.error && createSubResponse?.data?.isActive)) {
          setPerksSubscribeSuccessful({
            returningMember: currentPerksSubscription?.isActive,
            wasPreviousMember: Boolean(currentPerksSubscription?.id),
          });
        } else {
          setPerksSignupError(getErrorAlert(createSubResponse?.statusCode));
        }
      }
    } else {
      alert = getErrorAlert(currentPerksSubscription?.error);
    }

    if (!getPerksRedirectDestination()) {
      if (themeContext?.receiptCode) {
        receiptHelper.redirectToAccount(themeContext.receiptCode, {
          alert,
          gtmCategory: 'eReceipts - JB Perks',
        });
      } else {
        window.location.href = `${window.location.origin}/account#/?alert=${alert}&gtmCategory=${encodeURIComponent(
          'my account - JB Perks'
        )}`;
      }
    }
  };

  const { skipBirthdayPrompt, isPerksFinished } = themeContext ?? {};

  useEffect(() => {
    const query = new URL(window.location.href).searchParams;
    if (isMobileVerifyFinished && !customerLoading && !birthMonth && !skipBirthdayPrompt) {
      history.push('/add-birthday');
    } else if (birthMonth && query.get('source') === 'Birthmonthemail') {
      setThemeContext(themeContext => ({ ...themeContext, birthMonthExists: true }));
    }
  }, [isMobileVerifyFinished, customerLoading, birthMonth, skipBirthdayPrompt]);

  useEffect(() => {
    if (isMobileVerifyFinished && isPerksFinished && currentPerksSubscription?.queryStatus === 'success') {
      if (loadingMessage !== PERKS_FINISHING_UP_MESSAGE) {
        setLoadingMessage(PERKS_FINISHING_UP_MESSAGE);
      }
      handleFinish();
      if (!checkIsPerksRedirectSetAndNavigate()) {
        history.push('/');
      }
    }
  }, [isMobileVerifyFinished, isPerksFinished, currentPerksSubscription?.queryStatus]);

  useEffect(() => {
    if (birthMonth && isMobileVerifyFinished && (isJustVerified || currentMobileNumber)) {
      setThemeContext(themeContext => ({ ...themeContext, isPerksFinished: true }));
    }
  }, [birthMonth, isMobileVerifyFinished, isJustVerified, currentMobileNumber]);

  useEffect(() => {
    if (currentPerksSubscription?.error || customerError) {
      setThemeContext(themeContext => ({ ...themeContext, isPerksFinished: true }));
    }
  }, [currentPerksSubscription?.error, customerError]);

  useEffect(() => {
    const query = new URL(window.location.href).searchParams;
    setThemeContext(themeContext => ({
      ...themeContext,
      ignoreSkipVerifyPreference: true,
      hideSkipVerifyButton: true,
      receiptCode: query.get('receiptCode'),
      receiptPhone: query.get('phoneNumber'),
      source: query.get('source'),
      storeId: query.get('storeId'),
    }));
  }, []);

  return <>{children}</>;
};

export const PerksSignupWrapper = ({
  children,
  isSitewide,
}: {
  children: ReactNode;
  isSitewide?: boolean;
}): JSX.Element => {
  const url = new URL(window.location.href);

  return isSitewide && ['/pages/perks', '/account', '/account/login'].includes(url.pathname) ? (
    <>{children}</>
  ) : (
    <PerksSignup>{children}</PerksSignup>
  );
};
